
import Link from "next/link";
import { Logo } from "../logo";
import { contactInfo, legalLinks, navLinks, socialLinks } from "@/lib/site-config";
import { Linkedin, Facebook, Youtube, MapPin, Mail, Phone } from "lucide-react";

function XIcon({ className }: { className?: string }) {
    return (
        <svg className={className} fill="currentColor" viewBox="0 0 24 24"><path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 7.184L18.901 1.153zm-1.653 19.47h2.61l-10.59-12.088H7.058l10.192 12.088z"/></svg>
    )
}

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-dark-navy text-slate-300 pt-0 pb-16 relative z-20">
      <div className="w-full footer-wave mb-16"></div>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          <div>
            <Link href="/" aria-label="Back to homepage">
              <div className="flex items-center gap-2 mb-6 group">
                  <Logo className="h-14 w-auto" />
              </div>
            </Link>
            <p className="text-sm text-slate-400 mb-6 leading-relaxed">
              Empowering businesses with future-ready software solutions. Let's build something amazing together.
            </p>
            <div className="flex gap-4">
              <a aria-label="Follow us on LinkedIn" className="w-10 h-10 rounded-full bg-slate-800 hover:bg-[#0077b5] flex items-center justify-center transition-all duration-300 hover:scale-125 hover:shadow-[0_0_15px_rgba(0,119,181,0.6)] text-white" href={socialLinks.linkedin}>
                <Linkedin className="h-5 w-5" />
              </a>
              <a aria-label="Follow us on X" className="w-10 h-10 rounded-full bg-slate-800 hover:bg-black flex items-center justify-center transition-all duration-300 hover:scale-125 hover:shadow-[0_0_15px_rgba(0,0,0,0.6)] text-white" href={socialLinks.x}>
                <XIcon className="h-5 w-5" />
              </a>
              <a aria-label="Follow us on Facebook" className="w-10 h-10 rounded-full bg-slate-800 hover:bg-[#1877F2] flex items-center justify-center transition-all duration-300 hover:scale-125 hover:shadow-[0_0_15px_rgba(24,119,242,0.6)] text-white" href={socialLinks.facebook}>
                <Facebook className="h-5 w-5" />
              </a>
              <a aria-label="Follow us on YouTube" className="w-10 h-10 rounded-full bg-slate-800 hover:bg-[#FF0000] flex items-center justify-center transition-all duration-300 hover:scale-125 hover:shadow-[0_0_15px_rgba(255,0,0,0.6)] text-white" href={socialLinks.youtube}>
                <Youtube className="h-5 w-5" />
              </a>
            </div>
          </div>
          <div>
            <p className="text-white font-bold mb-6 text-lg">Contact Info</p>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3 group">
                <MapPin className="h-5 w-5 text-primary mt-0.5 group-hover:animate-pulse shrink-0" />
                <div>
                  <span className="font-semibold text-slate-200 block">Corporate Office</span>
                  <span>{contactInfo.address}</span>
                </div>
              </li>
              <li className="flex items-center gap-3 group">
                <Mail className="h-5 w-5 text-primary group-hover:animate-pulse" />
                <span>{contactInfo.email}</span>
              </li>
              <li className="flex items-center gap-3 group">
                <Phone className="h-5 w-5 text-primary group-hover:animate-pulse" />
                <span>{contactInfo.phone}</span>
              </li>
            </ul>
          </div>
          <div>
            <p className="text-white font-bold mb-6 text-lg">Quick Links</p>
            <ul className="space-y-3 text-sm">
              {navLinks.map(link => (
                <li key={link.href}><Link className="hover:text-primary transition-all hover:translate-x-2 inline-block hover:shadow-glow" href={link.href}>{link.label}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-white font-bold mb-6 text-lg">Legal</p>
            <ul className="space-y-3 text-sm">
              {legalLinks.map(link => (
                <li key={link.href}><Link className="hover:text-primary transition-colors" href={link.href}>{link.label}</Link></li>
              ))}
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center text-center md:text-left gap-4 text-xs text-slate-400">
          <p>&copy; 2022 - {currentYear} Darshni Software. All rights reserved.</p>
          <p>Designed with ❤️ by Darshni Software</p>
        </div>
      </div>
    </footer>
  );
}
